import { FC, useEffect, useState } from 'react';
import { Grid, Box, Typography, CircularProgress } from '@mui/material';
import { useDocumentService } from 'src/apis/documents/useDocumentService';
import { TDocumentDetails } from 'src/apis/documents/documents.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';

interface DocumentDetailsComponentProps {
  documentId: string;
}

const DocumentDetailsComponent: FC<DocumentDetailsComponentProps> = ({
  documentId,
}) => {
  const { translateText } = useTranslation();
  const { getDocument } = useDocumentService();
  const [document, setDocument] = useState<TDocumentDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    getDocument(documentId)
      .then((doc) => {
        setDocument(doc);
      })
      .catch((error) => {
        console.error('Error fetching document:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={{ xs: '300px', sm: '400px' }}
        data-testid="document-details-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!document) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2 } }} data-testid="document-details-error">
        <Typography
          variant="body1"
          color="error"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {translateText('common.errorLoadingData')}
        </Typography>
      </Box>
    );
  }

  const formatDate = (date?: Date | string) => {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString();
  };

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2 },
        bgcolor: 'background.paper',
        borderRadius: 1,
      }}
      data-testid="document-details-container"
    >
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Basic Details Section */}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
            }}
          >
            {translateText('documents.basicDetails')}
          </Typography>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              bgcolor: 'background.default',
              borderRadius: 1,
            }}
          >
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('common.name')}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {document.basic_details.name || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('documents.type')}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {document.basic_details.type || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('documents.size')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {document.basic_details.size
                    ? `${(document.basic_details.size / 1024).toFixed(2)} KB`
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('documents.verifiable')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {document.basic_details.is_verifiable
                    ? translateText('common.yes')
                    : translateText('common.no')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('documents.verified')}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  color={
                    document.basic_details.verified
                      ? 'success.main'
                      : 'text.secondary'
                  }
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {document.basic_details.verified
                    ? translateText('common.yes')
                    : translateText('common.no')}
                </Typography>
              </Grid>
              {document.basic_details.verified_at && (
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {translateText('common.verificationDate')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {formatDate(document.basic_details.verified_at)}
                  </Typography>
                </Grid>
              )}
              {document.basic_details.verification_notes && (
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {translateText('documents.verificationNotes')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {document.basic_details.verification_notes}
                  </Typography>
                </Grid>
              )}
              {document.basic_details.rejection_reason && (
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {translateText('documents.rejectionReason')}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="error.main"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {document.basic_details.rejection_reason}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>

        {/* Owner Section */}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
            }}
          >
            {translateText('documents.owner')}
          </Typography>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              bgcolor: 'background.default',
              borderRadius: 1,
            }}
          >
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('documents.ownerType')}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {document.owner.owner_type || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('documents.ownerName')}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {document.owner.owner_name || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('documents.ownerId')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {document.owner.owner_id || '-'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Verifier Section */}
        {document.verifier && (
          <Grid item xs={12}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              {translateText('documents.verifier')}
            </Typography>
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                bgcolor: 'background.default',
                borderRadius: 1,
              }}
            >
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {document.verifier.first_name && (
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('common.firstName')}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {document.verifier.first_name}
                    </Typography>
                  </Grid>
                )}
                {document.verifier.last_name && (
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('common.lastName')}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {document.verifier.last_name}
                    </Typography>
                  </Grid>
                )}
                {document.verifier.email && (
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('common.email')}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {document.verifier.email}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        )}

        {/* Institute Section */}
        {document.institute && (
          <Grid item xs={12}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              {translateText('documents.institute')}
            </Typography>
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                bgcolor: 'background.default',
                borderRadius: 1,
              }}
            >
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {document.institute.name && (
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('common.name')}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {document.institute.name}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        )}

        {/* Action Section */}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
            }}
          >
            {translateText('documents.action')}
          </Typography>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              bgcolor: 'background.default',
              borderRadius: 1,
            }}
          >
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('documents.createdAt')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {formatDate(document.action.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('documents.createdBy')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {document.action.created_by || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('documents.updatedAt')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {formatDate(document.action.updated_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('documents.updatedBy')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {document.action.updated_by || '-'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DocumentDetailsComponent;
